// inbox-95 base + inbox-101 SSR + inbox-112 SPA-shell fix.
//
// Дизайн:
// - Public marketing pages (/, /pricing, /features etc.) генерятся как
//   prerendered HTML через vite-ssg; client hydrate'ит существующий markup.
// - Cabinet/auth-страницы получают чистый spa-shell.html с пустым
//   `<div id="app"></div>` (без data-server-rendered).
//
// Hydration-mismatch trap: ViteSSG IIFE безусловно вызывает
// `app.mount('#app', true)` (isHydrate=true). На spa-shell без prerender'a
// Vue не находит соответствия → console-error «Hydration mismatch» + пустой DOM.
//
// Fix (inbox-112 prod incident): runtime-detect prerendered vs SPA-shell.
//  - prerendered (или SSR build) → используем ViteSSG как раньше (hydrate path)
//  - SPA-shell → манально createApp + mount без isHydrate
// На SSR build (`isClient=false`) всегда идёт ViteSSG-путь, чтобы prerender
// public-pages работал.

import { ViteSSG } from 'vite-ssg';
import { createApp as createVueApp } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { createPinia, type StateTree } from 'pinia';
import { createHead } from '@unhead/vue';
import App from './App.vue';
import { routes } from './router/routes';
import { useMeStore } from './stores/me';
// inbox-280 — Tailwind base/preflight ПЕРВЫМ, чтобы legacy main.css
// и scoped styles компонентов могли его перекрывать где надо.
import './index.css';
import './assets/main.css';

interface SetupContext {
  app: ReturnType<typeof createVueApp>;
  router: ReturnType<typeof createRouter>;
  initialState: { pinia?: Record<string, StateTree> };
}

function setupShared({ app, router, initialState }: SetupContext): void {
  const pinia = createPinia();
  app.use(pinia);

  const head = createHead();
  app.use(head);

  if (import.meta.env.SSR) {
    initialState.pinia = pinia.state.value as Record<string, StateTree>;
  } else if (initialState?.pinia) {
    pinia.state.value = initialState.pinia as Record<string, StateTree>;
  }

  router.afterEach((to) => {
    if (!import.meta.env.SSR && typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-route', String(to.name ?? ''));
    }
  });

  // inbox-268 — guard для /console/*: проверяет staff_role через
  // useMeStore.isStaff. Если me ещё не загружен — fetch'им; при denied
  // → redirect на /login (или /dashboard для уже залогиненных).
  router.beforeEach(async (to) => {
    if (!to.matched.some((r) => r.meta?.requiresStaff)) return true;
    if (import.meta.env.SSR) return true;
    const me = useMeStore();
    if (!me.initialized) await me.load();
    if (!me.isAuthenticated) return { name: 'login' };
    if (!me.isStaff) return { name: 'dashboard' };
    return true;
  });
}

const isClient = typeof window !== 'undefined';
const rootEl = isClient ? document.querySelector('#app') : null;
const isPrerendered = !!(rootEl && rootEl.hasAttribute('data-server-rendered'));
const isSpaShell = isClient && rootEl !== null && !isPrerendered;

if (isSpaShell) {
  // SPA-shell route → чистый client mount, без ViteSSG IIFE hydration.
  void (async () => {
    const app = createVueApp(App);
    const router = createRouter({
      history: createWebHistory(import.meta.env.BASE_URL),
      routes,
    });
    setupShared({ app, router, initialState: {} });
    app.use(router);
    await router.isReady();
    app.mount('#app');
  })();
}

// Export ViteSSG-based factory:
//  - SSR build (vite-ssg-build вызывает createApp(false, '/path')) → render
//  - Client prerendered route → ViteSSG IIFE автоматически вызывает
//    `app.mount('#app', true)` (hydrate)
//  - Client SPA-shell → ViteSSG НЕ вызывается (мы не triggers'им IIFE),
//    но export нужен для совместимости с auto-bootstrap'ом vite-ssg.
//    Trick: при isSpaShell стартуем noop-factory, чтобы IIFE внутри
//    ViteSSG не дублировал mount.
export const createApp = isSpaShell
  ? // SPA-shell: noop factory, real mount уже инициирован выше.
    (() => Promise.resolve()) as unknown as ReturnType<typeof ViteSSG>
  : ViteSSG(App, { routes, base: import.meta.env.BASE_URL }, setupShared);
